import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getUserOrders, deleteOrder, type Order } from '../../services/order.api';
import {
  Package,
  Loader2,
  ArrowRight,
  Calendar,
  AlertCircle,
  Search,
  ShoppingBag,
  ChevronRight,
  Wrench,
  Trash2,
} from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { cn } from '../../lib/utils';
import { getHeroClass } from '../../lib/design-system';
import { ROUTES, getOrderDetailRoute, getBookingDetailRoute } from '../../config/routes';
import { SmartBadge } from '../../components/orders-bookings';

const statusConfig = {
  placed: {
    label: 'Placed',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900',
  },
  processing: {
    label: 'Processing',
    badgeColor: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900',
  },
  shipped: {
    label: 'Shipped',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900',
  },
  out_for_delivery: {
    label: 'Out for Delivery',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/20 dark:text-indigo-400 dark:border-indigo-900',
  },
  delivered: {
    label: 'Delivered',
    badgeColor: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900',
  },
  installed: {
    label: 'Installed',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900',
  },
  completed: {
    label: 'Completed',
    badgeColor: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900',
  },
  canceled: {
    label: 'Canceled',
    badgeColor: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900',
  },
};

/** Order thumbnail — shows the product/asset image, falls back to an icon. */
function OrderThumb({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;
  return (
    <div className="h-20 w-20 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden border">
      {showImg ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-contain p-1.5"
          onError={() => setFailed(true)}
        />
      ) : (
        <Package className="h-8 w-8 text-muted-foreground/70" />
      )}
    </div>
  );
}

/** Slim linear delivery-progress bar with the current stage label. */
function OrderProgressBar({ status }: { status: string }) {
  const stageOrder = ['placed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
  const labels: Record<string, string> = {
    placed: 'Order Placed', processing: 'Processing', shipped: 'Shipped',
    out_for_delivery: 'Out for Delivery', delivered: 'Delivered',
    installed: 'Installed', completed: 'Completed', canceled: 'Canceled',
  };
  const idx = stageOrder.indexOf(status);
  const isCanceled = status === 'canceled';
  const isDone = ['delivered', 'installed', 'completed'].includes(status);
  const pct = isDone ? 100 : idx < 0 ? 8 : Math.max(8, (idx / (stageOrder.length - 1)) * 100);
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', isCanceled ? 'bg-red-400' : 'bg-[hsl(var(--brand-primary))]')}
          style={{ width: `${isCanceled ? 100 : pct}%` }}
        />
      </div>
      <span className="text-[11px] font-medium text-foreground whitespace-nowrap">
        {labels[status] ?? status}
      </span>
    </div>
  );
}

export function OrdersPage() {
  const { t } = useTranslation('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all'); // New: date filter
  const [confirmDelete, setConfirmDelete] = useState<Order | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const handleDeleteOrder = async () => {
    if (!confirmDelete) return;
    try {
      setDeletingId(confirmDelete.id);
      await deleteOrder(confirmDelete.orderId);
      setOrders((prev) => prev.filter((o) => o.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete order';
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };
  
  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserOrders();
      setOrders(data);
    } catch (err: unknown) {
      console.error('Failed to load orders:', err);
      const message = err instanceof Error ? err.message : 'Failed to load orders';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.productBrand || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.diagnosisIssue || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    // Date filter logic
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.createdAt);
      const now = new Date();
      const dayInMs = 24 * 60 * 60 * 1000;

      switch (dateFilter) {
        case 'recent':
          // Last 7 days
          matchesDate = (now.getTime() - orderDate.getTime()) <= 7 * dayInMs;
          break;
        case 'thisMonth':
          // Current month
          matchesDate = orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
          break;
        case 'last30Days':
          // Last 30 days
          matchesDate = (now.getTime() - orderDate.getTime()) <= 30 * dayInMs;
          break;
        case 'last90Days':
          // Last 90 days
          matchesDate = (now.getTime() - orderDate.getTime()) <= 90 * dayInMs;
          break;
        default:
          matchesDate = true;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  }).sort((a, b) => {
    // Newest first — guarantees just-placed orders appear at the top
    const aDate = new Date(a.orderDate || a.createdAt).getTime();
    const bDate = new Date(b.orderDate || b.createdAt).getTime();
    return bDate - aDate;
  });

  const activeCount = orders.filter(o =>
    ['placed', 'processing', 'shipped', 'out_for_delivery'].includes(o.status)
  ).length;
  const totalSpent = orders.reduce((sum, o) => sum + o.totalAmount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <div>
            <h3 className="font-medium text-lg">{t('loading.title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('loading.subtitle')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6 bg-background">
        <div className="text-center space-y-4 max-w-md">
          <div className="h-16 w-16 rounded-xl bg-red-50 dark:bg-red-950/20 flex items-center justify-center mx-auto border border-red-200 dark:border-red-900">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">{t('error.title')}</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <Button onClick={loadOrders} className="mt-4">
            <ArrowRight className="h-4 w-4 mr-2" />
            {t('button.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className={getHeroClass('main') + " mb-2"}>{t('title')}</h1>
            <p className="text-muted-foreground">
              {t('subtitle')}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-background">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{t('stats.order', { count: filteredOrders.length })}</span>
              </div>
              {activeCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-[hsl(var(--brand-primary)/0.07)] border-[hsl(var(--brand-primary)/0.25)]">
                  <span className="font-medium text-[hsl(var(--brand-primary))]">{t('stats.inTransit', { count: activeCount })}</span>
                </div>
              )}
              {totalSpent > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-background">
                  <span className="text-muted-foreground">{t('stats.total')}</span>
                  <span className="font-semibold">${totalSpent.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2 w-full sm:w-auto flex-wrap">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('search')}
                  className="pl-10 h-10 bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 rounded-lg border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">{t('filter.allStatus')}</option>
                <option value="placed">{t('filter.placed')}</option>
                <option value="processing">{t('filter.processing')}</option>
                <option value="shipped">{t('filter.shipped')}</option>
                <option value="out_for_delivery">{t('filter.outForDelivery')}</option>
                <option value="delivered">{t('filter.delivered')}</option>
                <option value="completed">{t('filter.completed')}</option>
                <option value="canceled">{t('filter.canceled')}</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-10 px-3 rounded-lg border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Time</option>
                <option value="recent">Last 7 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="last30Days">Last 30 Days</option>
                <option value="last90Days">Last 90 Days</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-muted mb-4">
              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {searchQuery || statusFilter !== 'all' ? t('empty.noResults') : t('empty.noOrders')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
              {searchQuery || statusFilter !== 'all' 
                ? t('empty.adjustSearch')
                : t('empty.description')
              }
            </p>
            {(searchQuery || statusFilter !== 'all') ? (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                {t('button.clearFilters')}
              </Button>
            ) : (
              <Button onClick={() => navigate(ROUTES.ASSETS)}>
                {t('button.browseAssets')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => {
              const status = statusConfig[order.status as keyof typeof statusConfig] || statusConfig.placed;
              const orderDate = new Date(order.orderDate || order.createdAt);
              const expectedDate = order.estimatedDelivery ? new Date(order.estimatedDelivery) : null;
              const daysUntilDelivery = expectedDate ? Math.ceil((expectedDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
              const isDone = order.status === 'delivered' || order.status === 'completed' || order.status === 'installed';

              return (
                <div
                  key={order.id}
                  onClick={() => navigate(getOrderDetailRoute(order.orderId))}
                  className="group flex gap-4 rounded-2xl border bg-card p-4 sm:p-5 cursor-pointer transition-all hover:shadow-md hover:border-[hsl(var(--brand-primary)/0.4)]"
                >
                  {/* Image */}
                  <OrderThumb
                    src={order.productImage || order.asset?.imageUrl || null}
                    alt={order.productName}
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex flex-col gap-2.5">
                    {/* Row 1: id/status + price */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border",
                            status.badgeColor
                          )}>
                            {t(`status.${order.status}`)}
                          </span>
                          <span className="text-[11px] text-muted-foreground font-mono truncate">#{order.orderId}</span>
                        </div>
                        <h3 className="font-semibold text-sm sm:text-base leading-snug line-clamp-1 group-hover:text-primary transition-colors">
                          {order.productName}
                        </h3>
                        {order.asset?.name ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{t('orderFor')} {order.asset.name}</p>
                        ) : order.partCategory ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {order.partCategory}{order.compatibleWith ? ` • ${order.compatibleWith}` : ''}
                          </p>
                        ) : null}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-base sm:text-lg leading-none">${order.totalAmount.toFixed(2)}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: slim progress */}
                    <OrderProgressBar status={order.status} />

                    {/* Row 3: footer */}
                    <div className="flex items-center justify-between gap-2 min-h-[1.25rem]">
                      {expectedDate && !isDone ? (
                        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          {t('expected')}{' '}
                          <span className="font-medium text-foreground">
                            {expectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          {daysUntilDelivery !== null && daysUntilDelivery <= 1 && (
                            <SmartBadge variant={daysUntilDelivery === 0 ? "today" : "tomorrow"} size="small" />
                          )}
                        </span>
                      ) : isDone ? (
                        <SmartBadge variant="ready_to_install" size="small" />
                      ) : <span />}

                      <div className="flex items-center gap-3 flex-shrink-0">
                        {order.relatedBookingId && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(getBookingDetailRoute(order.relatedBookingId!)); }}
                            className="flex items-center gap-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            <Wrench className="h-3.5 w-3.5" />
                            {t('button.viewBooking')}
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(order); }}
                          className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete order"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Chevron */}
                  <ChevronRight className="h-5 w-5 text-muted-foreground self-center flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <>
          <button
            type="button"
            aria-label="Close dialog"
            className="fixed inset-0 bg-black/50 z-50 cursor-default"
            onClick={() => !deletingId && setConfirmDelete(null)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm p-6 rounded-2xl bg-background border shadow-xl">
            <h3 className="text-base font-medium mb-1.5">Delete order?</h3>
            <p className="text-sm text-muted-foreground mb-5">
              <strong>{confirmDelete.productName}</strong> (#{confirmDelete.orderId}) will be
              permanently deleted. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(null)}
                disabled={!!deletingId}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteOrder}
                disabled={!!deletingId}
                className="gap-2"
              >
                {deletingId && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
